import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useAuth } from './auth/AuthContext';
import '../css/banding-helper.css';

function parseCri(entry) {
    const raw = entry?.cri ?? entry?.CRI ?? null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
}

function median(sortedAsc) {
    const n = sortedAsc.length;
    if (n === 0) return 0;
    return n % 2 ? sortedAsc[(n - 1) / 2] : (sortedAsc[n / 2 - 1] + sortedAsc[n / 2]) / 2;
}

function bandEvent(entries, opts = {}) {
    const {
        criKey = 'cri',
        minBandSize = 2,
        targetBands = null,
        gapFactor = 2.5
    } = opts;

    const cri = (entry) => Number(entry[criKey]);
    const sorted = [...entries].sort((a, b) => cri(b) - cri(a));
    if (sorted.length === 0) return [];

    const adjGaps = [];
    for (let i = 0; i < sorted.length - 1; i += 1) adjGaps.push(cri(sorted[i]) - cri(sorted[i + 1]));
    const positive = adjGaps.filter((gap) => gap > 0).sort((a, b) => a - b);
    const base = positive.length ? median(positive) : 0;
    const sigThreshold = gapFactor * base;

    let bands = [sorted];

    while (true) {
        if (targetBands && bands.length >= targetBands) break;

        let best = null;
        for (let bi = 0; bi < bands.length; bi += 1) {
            const band = bands[bi];
            if (band.length < 2 * minBandSize) continue;
            const lo = minBandSize - 1;
            const hi = band.length - 1 - minBandSize;
            for (let i = lo; i <= hi; i += 1) {
                const gap = cri(band[i]) - cri(band[i + 1]);
                if (!best || gap > best.gap) best = { bi, i, gap };
            }
        }
        if (!best) break;
        if (!targetBands && !(best.gap > 0 && best.gap >= sigThreshold)) break;

        const selected = bands[best.bi];
        bands.splice(best.bi, 1, selected.slice(0, best.i + 1), selected.slice(best.i + 1));
    }

    return bands;
}

function bandEntries(entries, opts = {}) {
    const { criKey = 'cri', eventKey = 'eventName', gapFactor = 2.5 } = opts;
    const cri = (entry) => Number(entry[criKey]);

    const byEvent = new Map();
    for (const entry of entries) {
        const key = entry[eventKey];
        if (!byEvent.has(key)) byEvent.set(key, []);
        byEvent.get(key).push(entry);
    }

    const result = [];
    for (const [eventName, list] of byEvent) {
        const rawBands = bandEvent(list, { ...opts, criKey });

        const sortedAll = [...list].sort((a, b) => cri(b) - cri(a));
        const gaps = [];
        for (let i = 0; i < sortedAll.length - 1; i += 1) gaps.push(cri(sortedAll[i]) - cri(sortedAll[i + 1]));
        const positive = gaps.filter((gap) => gap > 0).sort((a, b) => a - b);
        const significanceThreshold = positive.length ? gapFactor * median(positive) : 0;

        const bands = rawBands.map((crews, idx) => {
            const max = cri(crews[0]);
            const min = cri(crews[crews.length - 1]);
            const prev = idx > 0 ? rawBands[idx - 1] : null;
            const gapAbove = prev ? cri(prev[prev.length - 1]) - max : null;
            return {
                band: idx + 1,
                label: `${eventName} Band ${String.fromCharCode(65 + idx)}`,
                size: crews.length,
                criRange: [min, max],
                gapAbove,
                significant: gapAbove === null || gapAbove >= significanceThreshold,
                crews
            };
        });

        result.push({ eventName, significanceThreshold, bands });
    }

    return result;
}

function toBandingClipboardText(eventName, bandRows) {
    const splitPoints = [];
    for (let i = 0; i < bandRows.length - 1; i += 1) {
        splitPoints.push(bandRows[i].criRange[1]);
    }

    const lines = [
        `Event: ${eventName}`,
        splitPoints.length
            ? `Suggested split points (upper CRI per band): ${splitPoints.join(', ')}`
            : 'Suggested split points (upper CRI per band): none',
        ''
    ];

    bandRows.forEach((band) => {
        const crewNames = band.crews.map((entry) => `${entry.clubLabel} ${entry.name}`.trim()).join('; ');
        const softTag = band.significant ? '' : ' [soft split]';
        lines.push(`${band.label}${softTag}: CRI ${band.criRange[0]} to ${band.criRange[1]} (${band.size} crews)`);
        lines.push(`  ${crewNames}`);
    });

    return lines.join('\n');
}

function buildBandingForEvent(eventData) {
    if (!eventData) return { significanceThreshold: 0, bands: [] };

    const withCri = eventData.entries.filter((entry) => entry.cri !== null);
    const byEvent = bandEntries(withCri, { criKey: 'cri', eventKey: 'eventName' });
    if (!byEvent.length) return { significanceThreshold: 0, bands: [] };
    return {
        significanceThreshold: byEvent[0].significanceThreshold,
        bands: byEvent[0].bands
    };
}

export function BandingHelper() {
    const { user, getToken } = useAuth();
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [selectedEvent, setSelectedEvent] = useState('');
    const [copyMessage, setCopyMessage] = useState('');

    const loadEntries = useCallback(async () => {
        setLoading(true);
        setErrorMessage('');

        try {
            if (!user) {
                window.location.href = '/login';
                return;
            }

            const token = getToken();
            const response = await fetch('/api/meeting/entries', {
                credentials: 'include',
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.status === 401 || response.status === 403) {
                setErrorMessage('Auth error. Please logout and login again.');
                return;
            }

            if (!response.ok) {
                throw new Error(`Failed to load entries (HTTP ${response.status}).`);
            }

            const payload = await response.json();
            setEntries(Array.isArray(payload) ? payload : []);
        } catch (err) {
            console.error(err);
            setErrorMessage(err.message || 'Failed to load entries.');
        } finally {
            setLoading(false);
        }
    }, [getToken, user]);

    useEffect(() => {
        loadEntries();
    }, [loadEntries]);

    const events = useMemo(() => {
        const grouped = new Map();
        entries
            .filter((entry) => entry?.status !== 4)
            .forEach((entry) => {
                const eventName = String(entry?.parentEventName || entry?.eventName || '').trim();
                if (!eventName) return;

                const groupId = entry?.parentEventId ?? entry?.eventId ?? eventName;
                const groupKey = String(groupId);

                const normalized = {
                    ...entry,
                    cri: parseCri(entry),
                    clubLabel: String(entry?.titleMobile || entry?.titleAbbreviated || entry?.clubshortName || entry?.clubShortName || '').trim(),
                    name: String(entry?.name || '').trim()
                };

                if (!grouped.has(groupKey)) {
                    grouped.set(groupKey, {
                        eventKey: groupKey,
                        eventName,
                        entries: []
                    });
                }
                grouped.get(groupKey).entries.push(normalized);
            });

        return Array.from(grouped.values())
            .map((event) => ({
                ...event,
                crewCount: event.entries.length,
                criCount: event.entries.filter((entry) => entry.cri !== null).length
            }))
            .filter((event) => event.crewCount > 1)
            .sort((a, b) => a.eventName.localeCompare(b.eventName));
    }, [entries]);

    useEffect(() => {
        if (!events.length) {
            setSelectedEvent('');
            return;
        }

        if (!events.some((event) => event.eventKey === selectedEvent)) {
            setSelectedEvent(events[0].eventKey);
        }
    }, [events, selectedEvent]);

    const selectedEventData = useMemo(() => {
        return events.find((event) => event.eventKey === selectedEvent) || null;
    }, [events, selectedEvent]);

    const bandRows = useMemo(() => {
        if (!selectedEventData) return [];
        return buildBandingForEvent(selectedEventData).bands;
    }, [selectedEventData]);

    const visualBandingEvents = useMemo(() => {
        return events
            .map((eventData) => {
                const computed = buildBandingForEvent(eventData);
                return {
                    eventKey: eventData.eventKey,
                    eventName: eventData.eventName,
                    crewCount: eventData.criCount,
                    significanceThreshold: computed.significanceThreshold,
                    bands: computed.bands
                };
            })
            .filter((eventData) => eventData.bands.length > 1)
            .sort((a, b) => a.eventName.localeCompare(b.eventName));
    }, [events]);

    const visualAxisMax = useMemo(() => {
        const eventMax = visualBandingEvents
            .flatMap((eventData) => eventData.bands)
            .map((band) => band.criRange[1]);
        if (!eventMax.length) return 10;
        const rawMax = Math.max(...eventMax);
        return Math.max(10, Math.ceil(rawMax / 10) * 10);
    }, [visualBandingEvents]);

    const visualAxisTicks = useMemo(() => {
        const step = Math.max(10, Math.round(visualAxisMax / 4 / 10) * 10);
        const ticks = [0];
        let next = step;
        while (next < visualAxisMax) {
            ticks.push(next);
            next += step;
        }
        ticks.push(visualAxisMax);
        return ticks;
    }, [visualAxisMax]);

    const excludedCount = selectedEventData
        ? selectedEventData.entries.filter((entry) => entry.cri === null).length
        : 0;

    const handleCopy = async () => {
        if (!selectedEventData) return;

        const text = toBandingClipboardText(selectedEventData.eventName, bandRows);

        try {
            await navigator.clipboard.writeText(text);
            setCopyMessage('Copied banding summary.');
        } catch (err) {
            console.error('Clipboard copy failed:', err);
            setCopyMessage('Copy failed.');
        }

        window.setTimeout(() => setCopyMessage(''), 1500);
    };

    return (
        <div className='banding-helper-page'>
            <div className='banding-helper-header'>
                <h1>Banding Helper</h1>
                <p>Pick an event, confirm the suggested bands, and mirror the setup in BROE with minimal clicks.</p>
            </div>

            {errorMessage ? (
                <Alert variant='danger'>{errorMessage}</Alert>
            ) : null}

            <div className='banding-helper-controls'>
                <div className='banding-control-actions'>
                    <button className='btn btn-outline-secondary btn-sm' onClick={loadEntries} disabled={loading}>
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <button className='btn btn-primary btn-sm' onClick={handleCopy} disabled={!selectedEventData || !bandRows.length}>
                        Copy BROE Notes
                    </button>
                    {copyMessage ? <span className='banding-copy-message'>{copyMessage}</span> : null}
                </div>
            </div>

            <div className='banding-helper-layout'>
                <section className='banding-events-panel'>
                    <h2>Events</h2>
                    <div className='banding-events-list'>
                        {events.map((event) => (
                            <button
                                key={event.eventKey}
                                className={`banding-event-item ${selectedEvent === event.eventKey ? 'is-selected' : ''}`}
                                onClick={() => setSelectedEvent(event.eventKey)}
                                type='button'
                            >
                                <span>{event.eventName}</span>
                                <small>{event.crewCount} crews</small>
                            </button>
                        ))}
                        {!events.length ? <div className='banding-empty'>No events found.</div> : null}
                    </div>
                </section>

                <section className='banding-results-panel'>
                    <div className='banding-results-head'>
                        <h2>{selectedEventData?.eventName || 'Select an event'}</h2>
                        <div>
                            {selectedEventData ? `${selectedEventData.criCount} crews with CRI` : ''}
                        </div>
                    </div>

                    {excludedCount > 0 ? (
                        <Alert variant='warning'>
                            {excludedCount} crew{excludedCount === 1 ? '' : 's'} in this event have no CRI and were excluded.
                        </Alert>
                    ) : null}

                    <div className='banding-band-grid'>
                        {bandRows.map((band) => (
                            <div key={`band-${band.band}`} className='banding-band-card'>
                                <h3>{band.label}</h3>
                                <div className='banding-band-meta'>CRI {band.criRange[0]} to {band.criRange[1]}</div>
                                <div className='banding-band-meta'>{band.size} crews</div>
                                {band.gapAbove !== null ? (
                                    <div className='banding-band-meta'>Gap above: {band.gapAbove.toFixed(1)} CRI</div>
                                ) : null}
                                {!band.significant ? (
                                    <div className='banding-band-soft-flag'>Soft split (forced / non-dominant boundary)</div>
                                ) : null}
                                <ul>
                                    {band.crews.map((entry) => (
                                        <li key={entry.id}>
                                            <span className='banding-cri'>{entry.cri}</span>
                                            <span>{entry.clubLabel} {entry.name}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <section className='banding-visuals-panel'>
                <div className='banding-visuals-head'>
                    <h2>Banding Visuals</h2>
                    <div>Events with natural splits</div>
                </div>

                {visualBandingEvents.length ? (
                    <>
                        <div className='banding-visual-legend'>
                            <span className='banding-legend-item'>
                                <i className='banding-legend-dot banding-legend-dot-top' />
                                Top band
                            </span>
                            <span className='banding-legend-item'>
                                <i className='banding-legend-dot banding-legend-dot-main' />
                                Main band
                            </span>
                            <span className='banding-legend-item'>
                                <i className='banding-legend-dot banding-legend-dot-soft' />
                                Soft split
                            </span>
                        </div>

                        {visualBandingEvents.map((eventData) => (
                            <div className='banding-visual-event' key={`visual-${eventData.eventKey}`}>
                                <h3>{eventData.eventName} - {eventData.crewCount} crews</h3>
                                <div className='banding-visual-axis'>
                                    {visualAxisTicks.map((tick) => (
                                        <span key={`${eventData.eventKey}-tick-${tick}`}>
                                            {tick}
                                        </span>
                                    ))}
                                </div>

                                <div className='banding-visual-track'>
                                    {eventData.bands.map((band, index) => {
                                        const min = band.criRange[0];
                                        const max = band.criRange[1];
                                        const left = (min / visualAxisMax) * 100;
                                        const width = Math.max(2, ((max - min) / visualAxisMax) * 100);
                                        const kindClass = band.significant
                                            ? index === 0
                                                ? 'is-top'
                                                : 'is-main'
                                            : 'is-soft';

                                        return (
                                            <div
                                                key={`${eventData.eventKey}-band-${band.band}`}
                                                className={`banding-visual-band ${kindClass}`}
                                                style={{ left: `${left}%`, width: `${width}%` }}
                                                title={`${band.label}: ${band.criRange[0]}-${band.criRange[1]}`}
                                            >
                                                <span className='banding-visual-min'>{min}</span>
                                                <span className='banding-visual-max'>{max}</span>
                                            </div>
                                        );
                                    })}

                                    {eventData.bands
                                        .filter((band) => band.gapAbove !== null)
                                        .map((band) => {
                                            const upperBand = eventData.bands[band.band - 2];
                                            if (!upperBand) return null;
                                            const gapStart = band.criRange[1];
                                            const gapEnd = upperBand.criRange[0];
                                            const center = ((gapStart + gapEnd) / 2 / visualAxisMax) * 100;

                                            return (
                                                <div key={`${eventData.eventKey}-gap-${band.band}`} className='banding-visual-gap' style={{ left: `${center}%` }}>
                                                    {`${band.gapAbove} CRI gap`}
                                                </div>
                                            );
                                        })}
                                </div>
                            </div>
                        ))}
                    </>
                ) : (
                    <div className='banding-empty'>No events currently split into multiple bands.</div>
                )}
            </section>
        </div>
    );
}
